const translations = {
  en: {year: 'year', years: 'years', month: 'month', months: 'months'},
  de: {year: 'Jahr', years: 'Jahre', month: 'Monat', months: 'Monate'},
};

/**
 * NOTE: This function calculates and formats
 * the duration between two dates.
 * Full example: Jan. 2015–Heute · 8 Jahre 10 Monate
 * Date only example: Jan. 2013–Jan. 2015
 * Duration only example : 5 Jahre 2 Monate
 */
export const formatStartEndWithDuration = (
  locale: string,
  start: {month: number; year: number} | null,
  end?: {month: number; year: number},
  options?: {
    durationOnly?: boolean;
    datesOnly?: boolean;
  }
) => {
  if (!start) {
    return '';
  }
  const startDate = new Date(start.year, start.month - 1);
  const endDate = end ? new Date(end.year, end.month - 1) : new Date();

  const dateFormatter = new Intl.DateTimeFormat(locale, {year: 'numeric', month: 'short'});

  const formattedStartDate = dateFormatter.format(startDate);
  const formattedEndDate = end ? dateFormatter.format(endDate) : locale === 'de' ? 'Heute' : 'Present';

  let yearDiff = endDate.getFullYear() - startDate.getFullYear();
  let monthDiff = endDate.getMonth() - startDate.getMonth();
  if (monthDiff < 0) {
    yearDiff--;
    monthDiff += 12;
  }

  const numberFormat = new Intl.NumberFormat(locale);

  const selectedTranslation = translations[(locale as 'en') || 'de'] || translations['en'];

  const yearLabel = yearDiff === 1 ? selectedTranslation.year : selectedTranslation.years;
  const monthLabel = monthDiff === 1 ? selectedTranslation.month : selectedTranslation.months;

  const yearString = yearDiff > 0 ? `${numberFormat.format(yearDiff)} ${yearLabel}` : '';
  const monthString = monthDiff > 0 ? `${numberFormat.format(monthDiff)} ${monthLabel}` : '';

  if (options?.durationOnly) {
    return `${yearString} ${monthString}`.trim();
  } else if (options?.datesOnly) {
    return `${formattedStartDate}–${formattedEndDate}`.trim();
  }
  return `${formattedStartDate}–${formattedEndDate} · ${yearString} ${monthString}`.trim();
};

/**
 * NOTE: This function formats a simple duration
 * Example: 5 years
 * Example: 1 month
 */
export const formatSimpleDuration = (
  params: {amount: number; unit: 'years' | 'months' | 'year' | 'month'},
  locale: string
) => {
  if (params.amount === 0) {
    return '-';
  }

  const numberFormat = new Intl.NumberFormat(locale);
  const selectedTranslation = translations[locale as 'en' | 'de'] || translations['en'];
  const label = (() => {
    if (params.unit === 'years' || params.unit === 'year') {
      return params.amount === 1 ? selectedTranslation.year : selectedTranslation.years;
    } else if (params.unit === 'months' || params.unit === 'month') {
      return params.amount === 1 ? selectedTranslation.month : selectedTranslation.months;
    }
  })();
  return `${numberFormat.format(params.amount)} ${label}`;
};
